import { useEffect, useRef, useState } from 'react';

export type FlashDirection = 'up' | 'down' | null;

interface FlashState {
  direction: FlashDirection;
  prev:      number;
}

/**
 * usePriceFlash
 *
 * Tracks price changes for one or more symbols and returns a
 * "flash" direction ('up' | 'down' | null) per symbol.
 *
 * Usage:
 *   const flash = usePriceFlash(prices);
 *   flash['BTC'] → 'up' | 'down' | null
 *
 * The flash lasts 600ms then resets to null.
 */
export const usePriceFlash = (
  prices: Record<string, number>,
  duration = 600
): Record<string, FlashDirection> => {
  const prevRef  = useRef<Record<string, number>>({});
  const timers   = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const [flash, setFlash] = useState<Record<string, FlashDirection>>({});

  useEffect(() => {
    const updates: Record<string, FlashDirection> = {};
    let hasChange = false;

    for (const [symbol, price] of Object.entries(prices)) {
      const prev = prevRef.current[symbol];

      if (prev !== undefined && prev !== price) {
        updates[symbol] = price > prev ? 'up' : 'down';
        hasChange = true;

        // Clear any existing timer for this symbol
        if (timers.current[symbol]) clearTimeout(timers.current[symbol]);

        // Reset flash after duration
        timers.current[symbol] = setTimeout(() => {
          setFlash(f => ({ ...f, [symbol]: null }));
        }, duration);
      }

      prevRef.current[symbol] = price;
    }

    if (hasChange) {
      setFlash(f => ({ ...f, ...updates }));
    }
  }, [prices, duration]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(timers.current).forEach(clearTimeout);
    };
  }, []);

  return flash;
};

/**
 * useSinglePriceFlash
 *
 * Convenience hook for a single price value.
 *
 * Usage:
 *   const flash = useSinglePriceFlash(prices['BTC']);
 *   flash → 'up' | 'down' | null
 */
export const useSinglePriceFlash = (price: number, duration = 600): FlashDirection => {
  const prevRef = useRef<number | null>(null);
  const timer   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [flash, setFlash] = useState<FlashDirection>(null);

  useEffect(() => {
    if (prevRef.current === null) {
      prevRef.current = price;
      return;
    }

    if (price !== prevRef.current) {
      const dir: FlashDirection = price > prevRef.current ? 'up' : 'down';
      prevRef.current = price;

      if (timer.current) clearTimeout(timer.current);
      setFlash(dir);
      timer.current = setTimeout(() => setFlash(null), duration);
    }
  }, [price, duration]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return flash;
};

/**
 * flashClass
 *
 * Returns a Tailwind class string for the flash direction.
 * Use on any element wrapping a price display.
 *
 * @param direction  'up' | 'down' | null
 * @param base       default class when no flash (default: 'text-gray-50')
 */
export const flashClass = (
  direction: FlashDirection,
  base = 'text-gray-50'
): string => {
  if (direction === 'up')   return 'text-primary  transition-colors duration-150';
  if (direction === 'down') return 'text-red-500   transition-colors duration-150';
  return `${base} transition-colors duration-500`;
};

/**
 * flashBgClass
 *
 * Same idea but returns a background flash class — useful for table rows
 * or price ticker chips.
 */
export const flashBgClass = (direction: FlashDirection): string => {
  if (direction === 'up')   return 'bg-primary/10  transition-colors duration-150';
  if (direction === 'down') return 'bg-red-500/10  transition-colors duration-150';
  return 'transition-colors duration-500';
};
